import { replaceUnderscoresWithSpaces } from '../../../../utils/commonFunctions';

export interface Fault {
  faultSystem?: string;
  faultExecutionTime?: number;
  faultName?: string;
  status?: string;
  faultType?: string;
  faultDetails?: any;
  startTime?: number;
  faultId?: string;
  faultTaskId?: string;
  executionRequirement?: any;
  [key: string]: any;
}

export interface MappedFault {
  component?: string;
  time: number;
  message: string;
  status: string;
  key: string;
  executionRequirement?: any;
  faultDetails?: any;
  [key: string]: any;
}

/**
 * Format fault name by replacing underscores with spaces
 */
export const formatFaultLabel = (name?: string | null): string => {
  return name ? replaceUnderscoresWithSpaces(name) : "";
};

/**
 * Get display details from fault details object
 * Prefers 'display' sub-object when present, otherwise uses raw values
 */
export const getDisplayDetails = (faultDetails?: any): Record<string, any> => {
  if (!faultDetails || typeof faultDetails !== "object") return {};
  
  const display = faultDetails?.display;
  if (display && typeof display === "object" && Object?.keys(display)?.length > 0) {
    return Object?.fromEntries(
      Object?.entries(display).map(([k, v]) => [replaceUnderscoresWithSpaces(k), v])
    );
  }
  
  return Object.fromEntries(
    Object.entries(faultDetails)
      ?.filter(([k]) => k !== "display")
      ?.map(([k, v]) => [replaceUnderscoresWithSpaces(k), v])
  );
};

/**
 * Get execution time for a fault
 */
export const getFaultExecutionTime = (fault: Fault): number => {
  if (typeof fault?.startTime === "number" && !isNaN(fault.startTime)) {
    return fault.startTime;
  }
  if (typeof fault?.faultExecutionTime === "number" && !isNaN(fault.faultExecutionTime)) {
    return fault.faultExecutionTime;
  }
  return 0;
};

/**
 * Check if fault has valid execution time
 */
export const isValidFaultTime = (fault: Fault): boolean => {
  return (
    (typeof fault?.startTime === "number" && !isNaN(fault.startTime)) ||
    (typeof fault?.faultExecutionTime === "number" && !isNaN(fault.faultExecutionTime))
  );
};

/**
 * Get unique ID for a fault
 */
export const getFaultId = (fault: Fault, index?: number): string => {
  return fault?.faultId || fault?.faultTaskId || `fault-${index ?? 0}`;
};

/**
 * Get fault key for React rendering
 */
export const getFaultKey = (fault: Fault, index: number, keySuffix: number | string = index): string => {
  const id = getFaultId(fault, index);
  const time = getFaultExecutionTime(fault);
  return `${fault?.faultType || id}-${time}-${keySuffix}`;
};

/**
 * Map fault status to display format
 */
export const mapFaultStatus = (status?: string, dashboard?: string): string => {
  if (!status) return "";
  
  if (status === "Completed") return "Completed";
  if (status === "SCHEDULED") return "SCHEDULED";
  if (status === "NOT_SCHEDULE") return "NOT_SCHEDULE";
  
  return status;
};

/**
 * Compute summary status for dashboard
 */
export const computeSummaryStatus = (fault: Fault, currentTime?: number): string => {
  const terminalStatuses = ['CANCELLED', 'ABORTED', 'FAILED', 'COMPLETED'];
  
  if (fault?.status === 'CANCELLED') return 'CANCELLED';
  
  const start = getFaultExecutionTime(fault);
  const executedEnd = (typeof fault?.executedEndTime === "number" && !isNaN(fault.executedEndTime)) 
    ? fault.executedEndTime 
    : 0;
  const configuredEnd = (typeof fault?.endTime === "number" && !isNaN(fault.endTime)) 
    ? fault.endTime 
    : start;
  const end = executedEnd !== 0 ? executedEnd : configuredEnd;
  
  if (currentTime && currentTime < start) return 'SCHEDULED';
  if (currentTime && currentTime >= start && currentTime < end) {
    if (terminalStatuses?.includes(fault?.status ?? '')) return fault?.status ?? '';
    if (fault?.status === 'EXECUTING') return 'EXECUTING';
    return 'EXECUTING';
  }
  if (terminalStatuses?.includes(fault?.status ?? '')) return fault?.status ?? '';
  return 'COMPLETED';
};

/**
 * Deduplicate faults by ID
 */
export const dedupeFaultsById = (faultsArr: MappedFault[], getId: (fault: MappedFault) => string | undefined): MappedFault[] => {
  const seen = new Set<string>();
  return faultsArr?.filter(fault => {
    const id = getId(fault);
    if (!id || !isValidFaultTime(fault as Fault)) return false;
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  }) ?? [];
};

/**
 * Map fault to display format
 */
export const mapFaultToDisplay = (
  fault: Fault,
  index: number,
  dashboard?: string,
  currentTime?: number,
  keySuffix: number | string = index
): MappedFault => {
  const base = {
    ...fault,
    component: fault?.faultSystem,
    time: getFaultExecutionTime(fault),
    message: formatFaultLabel(fault?.message),
    faultName: formatFaultLabel(fault?.faultName),
    key: getFaultKey(fault, index, keySuffix),
    executionRequirement: fault?.executionRequirement,
    faultDetails: getDisplayDetails(fault?.faultDetails)
  };

  if (dashboard === 'summary' && currentTime !== undefined) {
    return {
      ...base,
      status: computeSummaryStatus(fault, currentTime)
    };
  }

  return {
    ...base,
    status: mapFaultStatus(fault?.status, dashboard)
  };
};

/**
 * Process fault data from various formats
 */
export const processFaultData = (
  faultData: any,
  dashboard?: string,
  currentTime?: number,
  customMapper?: (data: any) => MappedFault[]
): MappedFault[] => {
  if (customMapper) {
    return customMapper(faultData);
  }

  if (!faultData) return [];

  // Handle definedFaultTasks format
  if (faultData?.definedFaultTasks) {
    const mapped = (faultData.definedFaultTasks || []).map((f: Fault, i: number) => 
      mapFaultToDisplay(f, i, dashboard, currentTime, i)
    );
    return dedupeFaultsById(mapped, f => f?.faultId || f?.faultTaskId || f?.key);
  }

  // Handle array of objects with definedFaultTasks
  if (Array.isArray(faultData) && faultData?.[0]?.definedFaultTasks) {
    const mapped = faultData.flatMap((item: any) => 
      (item?.definedFaultTasks || []).map((f: Fault, i: number) => 
        mapFaultToDisplay(f, i, dashboard, currentTime, i)
      )
    );
    return dedupeFaultsById(mapped, f => f?.faultId || f?.faultTaskId || f?.key);
  }

  // Handle flat array
  if (Array.isArray(faultData)) {
    const mapped = faultData.map((f: Fault, i: number) => 
      mapFaultToDisplay(f, i, dashboard, currentTime, i)
    );
    return dedupeFaultsById(mapped, f => f?.faultId || f?.faultTaskId || f?.key);
  }

  return [];
};

/**
 * Get fault data from injected fault list
 */
export const getFaultDataFromList = (injectedFaultList: any): any => {
  return injectedFaultList?.data || injectedFaultList;
};

